import React from "react";
import { useSite } from "@/context/SiteContext";

const STATUS_TONE = {
  "Paid": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Partially Paid": "bg-amber-100 text-amber-700 border-amber-200",
  "Overdue": "bg-red-100 text-red-700 border-red-200",
  "Sent": "bg-blue-100 text-blue-700 border-blue-200",
  "Cancelled": "bg-neutral-200 text-neutral-600 border-neutral-300",
  "Draft": "bg-neutral-100 text-neutral-500 border-neutral-200",
};

// Premium, print-ready invoice document (white paper look) reused by admin, client & public views.
export const InvoiceDocument = ({ inv }) => {
  const site = useSite();
  if (!inv) return null;
  const cur = inv.currency || "\u20aa";
  const money = (n) => `${cur}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="invoice-print-area mx-auto w-full max-w-[800px] bg-white text-[#1a1a1a] rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
      <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg,#C8A14A,#E7CE93)" }} />
      <div className="p-8 sm:p-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={site.logo} alt="Ascendia Detail" className="h-12 w-12 object-contain" />
            <div>
              <p className="font-semibold text-lg tracking-tight">Ascendia <span style={{ color: "#B8860B" }}>Detail</span></p>
              <p className="text-xs text-neutral-500">{site.tagline || "Premium property detailing"}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold tracking-tight">INVOICE</p>
            <p className="text-sm text-neutral-500 mt-0.5">{inv.number}</p>
            <span className={`mt-2 inline-block text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${STATUS_TONE[inv.status] || "bg-neutral-100 text-neutral-600 border-neutral-200"}`}>{inv.status}</span>
          </div>
        </div>

        {/* Meta */}
        <div className="mt-8 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-neutral-400">Billed to</p>
            <p className="mt-1 font-medium">{inv.bill_to_name || "—"}</p>
            {inv.bill_to_email && <p className="text-sm text-neutral-500">{inv.bill_to_email}</p>}
            {inv.bill_to_phone && <p className="text-sm text-neutral-500">{inv.bill_to_phone}</p>}
            {inv.bill_to_address && <p className="text-sm text-neutral-500">{inv.bill_to_address}</p>}
          </div>
          <div className="text-right">
            <p className="text-sm"><span className="text-neutral-400">Issue date:</span> {(inv.issue_date || "").slice(0, 10)}</p>
            <p className="text-sm mt-1"><span className="text-neutral-400">Due date:</span> {(inv.due_date || "").slice(0, 10)}</p>
            {inv.notes && <p className="text-sm mt-1 text-neutral-500">{inv.notes}</p>}
          </div>
        </div>

        {/* Line items */}
        <table className="mt-8 w-full text-sm">
          <thead>
            <tr className="border-b-2 border-neutral-200 text-neutral-400 text-[11px] uppercase tracking-[0.12em]">
              <th className="text-left py-2 font-medium">Description</th>
              <th className="text-right py-2 font-medium w-16">Qty</th>
              <th className="text-right py-2 font-medium w-28">Unit</th>
              <th className="text-right py-2 font-medium w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.line_items || []).map((li, i) => (
              <tr key={i} className="border-b border-neutral-100">
                <td className="py-3 pr-2">{li.description}</td>
                <td className="py-3 text-right">{li.qty}</td>
                <td className="py-3 text-right">{money(li.unit_price)}</td>
                <td className="py-3 text-right font-medium">{money(li.amount != null ? li.amount : li.qty * li.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{money(inv.subtotal)}</span></div>
            {inv.discount > 0 && <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span>-{money(inv.discount)}</span></div>}
            {inv.vat_rate > 0 && <div className="flex justify-between"><span className="text-neutral-500">VAT ({inv.vat_rate}%)</span><span>{money(inv.vat_amount)}</span></div>}
            <div className="flex justify-between border-t-2 border-neutral-200 pt-2 text-base font-semibold"><span>Total</span><span>{money(inv.total)}</span></div>
            {inv.amount_paid > 0 && <div className="flex justify-between text-emerald-600"><span>Paid</span><span>-{money(inv.amount_paid)}</span></div>}
            <div className="flex justify-between text-base font-semibold" style={{ color: inv.balance > 0 ? "#B8860B" : "#059669" }}><span>Balance due</span><span>{money(inv.balance)}</span></div>
          </div>
        </div>

        {/* Terms + footer */}
        {inv.terms && <p className="mt-8 text-xs text-neutral-500 border-t border-neutral-100 pt-4">{inv.terms}</p>}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-400">
          <span>{site.phone}</span>
          <span>{site.email || "contact.ascendiahq@gmail.com"}</span>
          <span>Thank you for choosing Ascendia.</span>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDocument;
